import React, { useEffect, useMemo, useState } from 'react';
import { injectIntl } from 'react-intl';
import { DataGrid } from '@mui/x-data-grid';
import ReactMarkdown from 'react-markdown';
import UserAvatar from './UserAvatar';

// Type of pluginUserData is of the form: {
//  columnTitle: string;
//  value: string
// }
const PluginsTable = (props) => {
  const {
    pluginUserDataColumnTitleList,
    allUsers, intl, pluginUserDataCardTitle,
  } = props;

  const [allUserPluginInformation, setAllUserPluginInformation] = useState({});

  const commonUserProps = {
    field: 'User',
    headerName: intl.formatMessage({ id: 'app.learningDashboard.pluginsTable.userLabel', defaultMessage: 'User' }),
    flex: 1,
    sortable: true,
  };

  const commonCountProps = {
    field: 'count',
    headerName: intl.formatMessage({ id: 'app.learningDashboard.pluginsTable.totalLabel', defaultMessage: 'Total' }),
    flex: 1,
    sortable: true,
  };

  const gridCols = [
    {
      ...commonUserProps,
      valueGetter: (params) => params?.row?.User?.name,
      renderCell: (params) => (
        <>
          <div className="relative hidden w-8 h-8 rounded-full md:block">
            <UserAvatar user={params?.row?.User} />
          </div>
          <div className="mx-2 font-semibold text-gray-700">{params?.value}</div>
        </>
      ),
    },
  ];

  useEffect(() => {
    const hasUserInformationChanged = () => {
      const newUserIds = Object.keys(allUsers);
      const oldUserIds = Object.keys(allUserPluginInformation);

      const hasLengthChanged = (
        newUserIds.length !== oldUserIds.length);

      const hasKeysChanged = hasLengthChanged
        || newUserIds.some((uId) => !oldUserIds.includes(uId))
        || oldUserIds.some((uId) => !newUserIds.includes(uId));
      if (hasKeysChanged) {
        return true;
      }
      const hasPluginInformationChanged = Object.values(allUsers).some((newU) => {
        const oldU = allUserPluginInformation[newU.userKey];

        const oldUPluginDataList = oldU?.pluginUserData?.[pluginUserDataCardTitle] || [];
        const newUPluginDataKeys = newU?.pluginUserData?.[pluginUserDataCardTitle] || [];

        const hasPluginDataKeyChanged = oldUPluginDataList.length !== newUPluginDataKeys.length
          || oldUPluginDataList.some((oldUGDEntry) => {
            const newUPluginDataList = newUPluginDataKeys.filter(
              (newUGDKey) => oldUGDEntry.columnTitle === newUGDKey.columnTitle,
            );
            if (newUPluginDataList.length === 0) return true;
            const newPluginDataContainsOldItem = newUPluginDataList.some(
              (newUPluginDataItem) => newUPluginDataItem.value === oldUGDEntry.value,
            );
            return !newPluginDataContainsOldItem;
          });
        return hasPluginDataKeyChanged;
      });
      return hasPluginInformationChanged;
    };

    if (hasUserInformationChanged()) setAllUserPluginInformation(allUsers);
  }, [allUsers]);

  gridCols.push({
    ...commonCountProps,
    valueGetter: (params) => [...(new Set(Object.keys(
      params?.row?.User?.pluginUserData?.[pluginUserDataCardTitle],
    )))].length || 0,
    renderCell: (params) => params?.value,
  });

  pluginUserDataColumnTitleList.map((pluginColumnTitle) => {
    const commonColProps = {
      field: pluginColumnTitle,
      headerName: pluginColumnTitle,
      flex: 1,
    };
    gridCols.push({
      ...commonColProps,
      valueGetter: (
        params,
      ) => params?.row?.[pluginColumnTitle],
      renderCell: (params) => params?.value,
    });
    return pluginColumnTitle;
  });
  const gridRows = useMemo(() => Object.values(allUserPluginInformation).filter(
    (u) => !(Object.keys(u?.pluginUserData)?.length === 0),
  ).map((u, i) => ({
    id: i + 1,
    User: u,
    // This is going to be of the form:
    // [learningAnalyticsDashboardColumnTitle]: learningAnalyticsDashboardValue, for each entry
    ...u.pluginUserData?.[pluginUserDataCardTitle].reduce((acc, curr) => {
      const {
        columnTitle,
        value,
      } = curr;
      acc[columnTitle] = (
        <ReactMarkdown
          components={{
            a: ({ href, children }) => (
              <a href={href} className="text-blue-600 underline">
                {children}
              </a>
            ),
            img: ({
              node, src, alt, ...restProps
            }) => (
              <a href={src} target="_blank" rel="noopener noreferrer" className="block border border-gray-300">
                <img
                  src={src}
                  alt={alt}
                  {...restProps}
                  style={{
                    maxHeight: '120px',
                    height: 'auto',
                    maxWidth: '120px',
                    width: 'auto',
                    cursor: 'pointer',
                  }}
                />
              </a>
            ),
          }}
        >
          {value}
        </ReactMarkdown>
      );
      return acc;
    }, {}),
  })), [allUserPluginInformation]);

  const commonGridProps = {
    autoHeight: true,
    hideFooter: true,
    disableColumnMenu: true,
    disableColumnSelector: true,
    disableSelectionOnClick: true,
    rowHeight: 125,
  };
  return (
    <div className="bg-white" style={{ width: '100%' }}>
      <DataGrid
        {...commonGridProps}
        rows={gridRows}
        columns={gridCols}
        sortingOrder={['asc', 'desc']}
        sx={{
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: 'rgb(243 244 246/var(--tw-bg-opacity))',
            color: 'rgb(55 65 81/1)',
            textTransform: 'uppercase',
            letterSpacing: '.025em',
            minHeight: '40.5px !important',
            maxHeight: '40.5px !important',
            height: '40.5px !important',
          },
          '& .MuiDataGrid-virtualScroller': {
            marginTop: '40.5px !important',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: '600',
            fontSize: 'smaller !important',
          },
        }}
      />
    </div>
  );
};

export default injectIntl(PluginsTable);
